import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { Entry, Layer } from "shared";
import { resolveLayerColor } from "shared";
import { setShareToken } from "@/api/client";
import { entriesApi } from "@/api/entries";
import { mapsApi } from "@/api/maps";
import { useApi } from "@/api/hooks";
import { useToast } from "@/components/feedback/Toast";
import { AppShell } from "@/components/layout/AppShell";
import { buildEntryListItems } from "@/components/map/EntriesList";
import { viewportForEntries } from "@/components/map/fitEntries";
import { LayerPanel } from "@/components/map/LayerPanel";
import {
	MapView,
	type MapLayerData,
	type ViewportCommand,
} from "@/components/map/MapView";
import { Button } from "@/components/primitives/Button";
import { IconButton } from "@/components/primitives/IconButton";
import { IconChevronLeft } from "@/components/primitives/icons";
import { useSession } from "@/session/SessionProvider";

export default function MapViewerPage() {
	const { mapId = "" } = useParams();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("t");
	const navigate = useNavigate();
	const { toast } = useToast();
	const { isAuthed } = useSession();

	useEffect(() => {
		setShareToken(token);
		return () => setShareToken(null);
	}, [token]);

	const { data, loading, error } = useApi(
		() => mapsApi.get(mapId),
		[mapId, token],
		Boolean(mapId),
	);

	const [layers, setLayers] = useState<Layer[]>([]);
	const [entriesByLayer, setEntriesByLayer] = useState<Record<string, Entry[]>>({});
	const [visibility, setVisibility] = useState<Record<string, boolean>>({});
	const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
	const [entriesLoading, setEntriesLoading] = useState(false);
	const [viewportCommand, setViewportCommand] = useState<ViewportCommand | null>(null);
	const viewportKeyRef = useRef(0);
	const fittedMapIdRef = useRef<string | null>(null);
	const entriesByLayerRef = useRef(entriesByLayer);
	entriesByLayerRef.current = entriesByLayer;

	useEffect(() => {
		if (!data) return;
		setLayers(data.layers);
		const vis: Record<string, boolean> = {};
		for (const layer of data.layers) {
			vis[layer.id] = layer.defaultVisible;
		}
		setVisibility(vis);
	}, [data]);

	const loadEntries = useCallback(
		async (layerList: Layer[]) => {
			if (layerList.length === 0) {
				setEntriesByLayer({});
				return;
			}
			setEntriesLoading(true);
			try {
				const pairs = await Promise.all(
					layerList.map(async (layer) => {
						const res = await entriesApi.list(layer.id);
						return [layer.id, res.entries] as const;
					}),
				);
				const next: Record<string, Entry[]> = {};
				for (const [id, entries] of pairs) {
					next[id] = entries;
				}
				setEntriesByLayer(next);
			} catch (e) {
				toast(e instanceof Error ? e.message : "Einträge laden fehlgeschlagen", "error");
			} finally {
				setEntriesLoading(false);
			}
		},
		[toast],
	);

	useEffect(() => {
		if (layers.length === 0) {
			setEntriesByLayer({});
			return;
		}
		void loadEntries(layers);
	}, [layers, loadEntries]);

	useEffect(() => {
		fittedMapIdRef.current = null;
	}, [mapId]);

	useEffect(() => {
		if (entriesLoading || !mapId) return;
		if (fittedMapIdRef.current === mapId) return;
		const target = viewportForEntries(entriesByLayer);
		if (!target) return;
		fittedMapIdRef.current = mapId;
		viewportKeyRef.current += 1;
		setViewportCommand({ ...target, key: viewportKeyRef.current });
	}, [entriesByLayer, entriesLoading, mapId]);

	const mapLayers: MapLayerData[] = useMemo(
		() =>
			layers.map((layer, index) => ({
				id: layer.id,
				title: layer.title,
				color: resolveLayerColor(layer.style, index),
				visible: visibility[layer.id] ?? layer.defaultVisible,
				entries: entriesByLayer[layer.id] ?? [],
			})),
		[layers, visibility, entriesByLayer],
	);

	const entryItems = useMemo(
		() => buildEntryListItems(layers, entriesByLayer),
		[layers, entriesByLayer],
	);

	const totalEntries = entryItems.length;
	const canEdit = data?.access.role === "owner" || data?.access.role === "editor";

	const handleSelectEntry = (entryId: string | null, opts?: { fly?: boolean }) => {
		setSelectedEntryId(entryId);
		if (!entryId) return;
		const entry = Object.values(entriesByLayerRef.current)
			.flat()
			.find((e) => e.id === entryId);
		if (!entry || opts?.fly === false) return;

		const [minLng, minLat, maxLng, maxLat] = entry.bbox;
		const center: [number, number] = [
			(minLng + maxLng) / 2,
			(minLat + maxLat) / 2,
		];
		const isPoint =
			entry.geomType === "point" ||
			(minLng === maxLng && minLat === maxLat);
		viewportKeyRef.current += 1;
		setViewportCommand({
			center,
			bbox: isPoint ? null : entry.bbox,
			zoom: isPoint ? 15 : undefined,
			key: viewportKeyRef.current,
		});
	};

	if (loading) {
		return (
			<div className="flex min-h-svh items-center justify-center text-sm text-fg-muted">
				Karte laden…
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="mx-auto flex min-h-svh max-w-lg flex-col items-center justify-center px-4 text-center">
				<p className="text-rose-500">{error?.message ?? "Karte nicht gefunden"}</p>
				<p className="mt-2 text-sm text-fg-muted">
					Link ungültig, widerrufen, oder Karte ist privat.
				</p>
				<Button as="a" href={isAuthed ? "/app" : "/"} variant="secondary" className="mt-6">
					Zur Startseite
				</Button>
			</div>
		);
	}

	const { map } = data;
	const center = map.viewState?.center ?? [10.45, 51.16];
	const zoom = map.viewState?.zoom ?? 5.5;
	const editHref = token
		? `/k/${mapId}?t=${encodeURIComponent(token)}`
		: `/k/${mapId}`;

	return (
		<AppShell
			mode="map"
			mapBar={
				<header className="absolute inset-x-0 top-0 z-30 flex h-12 items-center gap-2 border-b border-zinc-950/5 bg-surface/85 px-2 backdrop-blur-md dark:border-white/5 sm:px-3">
					<IconButton
						label="Zurück"
						onClick={() => navigate(isAuthed ? "/app" : "/")}
					>
						<IconChevronLeft />
					</IconButton>
					<div className="min-w-0 flex-1">
						<div className="flex min-w-0 items-center gap-2">
							<p className="truncate text-sm font-medium text-fg">{map.title}</p>
							<span className="shrink-0 rounded-[var(--radius-control)] bg-zinc-950/[0.06] px-1.5 py-0.5 text-[11px] font-medium text-fg-muted dark:bg-white/10">
								Lesemodus
							</span>
						</div>
						<p className="truncate text-xs text-fg-muted">
							{layers.length} Ebene{layers.length === 1 ? "" : "n"} · {totalEntries}{" "}
							Eintrag
							{totalEntries === 1 ? "" : "e"}
							{entriesLoading ? " · laden…" : ""}
						</p>
					</div>
					<div className="flex shrink-0 items-center gap-1">
						{canEdit ? (
							<Button as="a" href={editHref} size="sm" variant="secondary">
								Bearbeiten
							</Button>
						) : null}
						{isAuthed ? (
							<Button as="a" href="/app" size="sm" variant="ghost">
								Karten
							</Button>
						) : (
							<Button as="a" href="/login" size="sm" variant="ghost">
								Anmelden
							</Button>
						)}
					</div>
				</header>
			}
		>
			<div className="relative h-svh w-full overflow-hidden">
				<MapView
					center={center}
					zoom={zoom}
					className="absolute inset-0 h-full w-full"
					layers={mapLayers}
					drawMode="select"
					selectedEntryId={selectedEntryId}
					draftVertices={[]}
					viewportCommand={viewportCommand}
					onSelectEntry={(id) => handleSelectEntry(id, { fly: false })}
					onMapClick={() => {}}
				/>
				<div className="absolute inset-x-0 bottom-0 z-20 max-md:max-h-[45vh] md:inset-x-auto md:bottom-auto md:left-3 md:top-14 md:w-80">
					<LayerPanel
						layers={layers}
						activeLayerId={null}
						visibility={visibility}
						onSelect={() => {}}
						onToggleVisibility={(id) => {
							setVisibility((prev) => ({
								...prev,
								[id]: !(prev[id] ?? true),
							}));
						}}
						onColorChange={() => {}}
						onRename={() => {}}
						onDelete={() => {}}
						onAdd={() => {}}
						readOnly
						entryItems={entryItems}
						selectedEntryId={selectedEntryId}
						onSelectEntry={(id) => handleSelectEntry(id)}
						entriesLoading={entriesLoading}
						className="max-md:max-w-none"
					/>
				</div>
			</div>
		</AppShell>
	);
}
